// src/App.js
import React, { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import "./App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { GoogleMapsProvider } from "./context/GoogleMapsProvider";
import { Toaster } from "react-hot-toast";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LocationProvider } from "./providers/LocationProvider";
import { CompareProvider } from "./providers/CompareProvider";
import CompareBar from "./components/compareBar/CompareBar";
import InstallAppPrompt from "./components/installAppPrompt/InstallAppPrompt";

// import { useLocation } from "react-router-dom";

// Pages & Components
import CreateAccount from "./onboardingPage/CreateAccount";
import LoginPage from "./onboardingPage/LoginScreen";
import ResetPasswordPage from "./onboardingPage/ResetPassword";
import PasswordResetConfirmPage from "./onboardingPage/PasswordResetConfirm";
import DecisionScreen from "./onboardingPage/DecisionScreen";
import TutorRegistration from "./components/tutorRegistration/TutorRegistration";
import AcademyRegistration from "./components/academyRegistration/AcademyRegistration";
import TutorsHome from "./pages/tutorsHome/TutorsHome";
import AcademyHome from "./pages/academyHome/AcademyHome";
import StudentHome from "./pages/studentHome/StudentHome";
import HomePage from "./pages/homePage/HomePage";
import AboutPage from "./pages/aboutPage/AboutPage";
import GetHelpPage from "./pages/getHelpPage/GetHelpPage";
import TutorProfilePage from "./pages/aboutPage/tutorProfilePage/TutorProfilePage";
import TermsAndConditions from "./pages/termsAndConditions/TermsAndConditions";
import PrivacyPolicy from "./pages/privacyPolicy/PrivacyPolicy";
import ScrollToHash from "./components/scrollToHash/ScrollToHash";
import SearchResults from "./pages/searchResults/SearchResults";
import {
  TutorsLocationSubjectLanding,
  TutorsCombinedSlugLanding,
  AdultEducationServiceLanding,
  TrainingCentresLocationLanding,
} from "./pages/categoryLanding/CategoryLandingPages";
import ComparePage from "./pages/comparePage/ComparePage";
import PageViewTracker from "./components/pageViewTracker/PageViewTracker";
import ChallengePage from "./pages/challengePage/ChallengePage";
import SupportPage from "./pages/supportPage/SupportPage";
import CodeOfConduct from "./pages/codeOfConduct/CodeOfConduct";
import ChildProtectionPolicy from "./pages/childProtectionPolicy/ChildProtectionPolicy";
import RefundCancellation from "./pages/refundAndCancellationPolicy/refundAndCancellationPolicy";
import BlogPage from "./pages/blogPage/BlogPage";
import SingleBlogPage from "./pages/blogPage/SingleBlogPage";
import AccountPage from "./pages/account/AccountPage";
import SubscriptionSection from "./pages/account/sections/SubscriptionSection";
import SubscriptionManagement from "./pages/subscriptions/SubscriptionManagement";
import CookieConsent from "./components/cookieConsent/CookieConsent";
import LocationConsent from "./components/locationConsent/LocationConsent";
import UserDashboard from "./components/dashboard/UserDashboard";
import SubscriptionCallback from "./pages/subscriptions/SubscriptionCallback";
import SpinnerOverlay from "./components/spinnerOverlay/SpinnerOverlay";
import UserChatPanel from "./components/userChatPanel/UserChatPanel";
import UserChatWidget from "./components/userChatPanel/UserChatWidget";

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 5 * 60 * 1000, retry: 2 } },
});

// ✅ ProtectedRoute stays here — inside Router so useAuth + Navigate both work
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const tokenExists = !!localStorage.getItem("token");
  const storedUser = tokenExists
    ? JSON.parse(localStorage.getItem("user") || "null")
    : null;

  const activeUser = user || storedUser;

  if (!activeUser) {
    return <Navigate to="/" replace />;
  }

  if (activeUser.profileCompleted === false) {
    if (activeUser.role === "tutor") {
      return <Navigate to="/tutor/registration" replace />;
    }
    if (activeUser.role === "institution") {
      return <Navigate to="/academy/registration" replace />;
    }
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(activeUser.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const GlobalWidgets = () => {
  const { user } = useAuth();
  const location = useLocation();
  const hideOn = ["/login", "/register"];

  if (hideOn.includes(location.pathname)) return null;
  return (
    <>
      <UserChatWidget userAvatar={user?.picture?.url} />
      <CompareBar />
      <InstallAppPrompt />
    </>
  );
};

const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <div className="App">
      <LocationConsent />
      <CookieConsent />
      <ScrollToHash />
      <PageViewTracker />
      <GlobalWidgets />
      <Suspense fallback={<SpinnerOverlay />}>
        {" "}
        {/* you already have this component! */}
        <Routes>
          {/* ── Public Routes ── */}
          <Route path="/" element={<HomePage />} />
          <Route path="/about-us" element={<AboutPage />} />
          <Route path="/get-help" element={<GetHelpPage />} />
          <Route path="/create-account" element={<CreateAccount />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/reset-confirm" element={<PasswordResetConfirmPage />} />
          <Route path="/search" element={<SearchResults />} />
          <Route path="/compare" element={<ComparePage />} />
          {/* ── SEO-friendly category/location landing routes ──
              Thin wrappers around SearchResults — see CategoryLandingPages.jsx */}
          <Route
            path="/tutors/:location/:subject"
            element={<TutorsLocationSubjectLanding />}
          />
          <Route
            path="/tutors/:combinedSlug"
            element={<TutorsCombinedSlugLanding />}
          />
          <Route
            path="/adult-education"
            element={<AdultEducationServiceLanding />}
          />
          <Route
            path="/adult-education/:serviceSlug"
            element={<AdultEducationServiceLanding />}
          />
          <Route
            path="/training-centres"
            element={<TrainingCentresLocationLanding />}
          />
          <Route
            path="/training-centres/:location"
            element={<TrainingCentresLocationLanding />}
          />
          <Route path="/award" element={<ChallengePage />} />
          <Route path="/abiri-support" element={<SupportPage />} />
          <Route path="/decision-screen" element={<DecisionScreen />} />
          <Route path="/tutor/registration" element={<TutorRegistration />} />
          <Route
            path="/academy/registration"
            element={<AcademyRegistration />}
          />
          <Route path="/tutor/:id" element={<TutorProfilePage />} />
          <Route path="/academy/:id" element={<TutorProfilePage />} />
          <Route
            path="/terms-and-conditions"
            element={<TermsAndConditions />}
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/code-of-conduct" element={<CodeOfConduct />} />
          <Route
            path="/child-protection-policy"
            element={<ChildProtectionPolicy />}
          />
          <Route
            path="/refund-cancellation-policy"
            element={<RefundCancellation />}
          />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<SingleBlogPage />} />
          {/* ── Subscription callback (public — Paystack redirects here) ── */}
          <Route
            path="/subscription/callback"
            element={<SubscriptionCallback />}
          />
          {/* ── Protected Routes ── */}
          <Route
            path="/student-home"
            element={
              <ProtectedRoute allowedRoles={["student"]}>
                <StudentHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tutor-home"
            element={
              <ProtectedRoute allowedRoles={["tutor"]}>
                <UserDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/academy-home"
            element={
              <ProtectedRoute allowedRoles={["institution"]}>
                <UserDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <AccountPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscriptions"
            element={
              <ProtectedRoute allowedRoles={["tutor", "institution"]}>
                <SubscriptionSection />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription-management"
            element={
              <ProtectedRoute allowedRoles={["tutor", "institution"]}>
                <SubscriptionManagement />
              </ProtectedRoute>
            }
          />
          {/* ── Fallback — must be last ── */}
          <Route path="*" element={<Navigate to={"/"} replace />} />
        </Routes>
      </Suspense>
    </div>
  );
};

// ✅ Router is now the outermost wrapper so ALL hooks (useNavigate, useLocation)
//    work everywhere including Header and AuthContext consumers
function App() {
  return (
    <AuthProvider>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            borderRadius: "8px",
            background: "#00b8FF",
            color: "#fff",
            zIndex: 9999,
          },
        }}
      />
      <Router>
        <QueryClientProvider client={queryClient}>
          <GoogleMapsProvider>
            <LocationProvider>
              <CompareProvider>
                <AppRoutes />
              </CompareProvider>
            </LocationProvider>
          </GoogleMapsProvider>
        </QueryClientProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;
